'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SkillGroup {
  category: string;
  color: string;
  icon: string;
  skills: { name: string; level: number }[];
}

const SKILL_GROUPS: SkillGroup[] = [
  {
    category: 'LANGUAGES',
    color: 'var(--cyber-cyan)',
    icon: 'ComputerDesktopIcon',
    skills: [
      { name: 'C / C++', level: 97 },
      { name: 'Python',      level: 94 },
      { name: 'Java',    level: 92 },
      { name: 'Javascript',   level: 85 },
      { name: 'MySQL',level: 72 },
    ],
  },
  {
    category: 'Core Competencies',
    color: 'var(--cyber-yellow)',
    icon: 'ServerIcon',
    skills: [
      { name: 'Data Structures & Algorithms', level: 92 },
      { name: 'Object Oriented Programming',  level: 78 },
      { name: 'Database Management System',   level: 88 },
      { name: 'Software Development Life Cycle',   level: 88 },
      { name: 'Operating System',  level: 82 },
      { name: 'System Design',   level: 85 },
    ],
  },
  {
    category: 'Web Development',
    color: 'var(--cyber-green)',
    icon: 'CloudIcon',
    skills: [
      { name: 'React.js / Typescript',  level: 86 },
      { name: 'Node.js / Express.js',  level: 90 },
      { name: 'HTML / CSS', level: 88 },
      { name: 'Spring Boot',     level: 74 },
      { name: 'REST APIs',  level: 91 },
    ],
  },
];

const TOOL_BADGES = [
  'GIT', 'AWS', 'PowerBI', 'Docker', 'AI','NOTION', 'SLACK','Postman'
];

export default function SkillsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const barsRef    = useRef<HTMLDivElement[]>([]);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !animated) {
            setAnimated(true);
            // Animate skill bars
            setTimeout(() => {
              barsRef.current.forEach((bar) => {
                if (bar) bar.classList.add('animate');
              });
            }, 200);
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [animated]);

  return (
    <section
      id="skills"
      ref={sectionRef}
      className="relative py-24 px-6 md:px-12 overflow-hidden"
      style={{ background: 'var(--cyber-dark)' }}
      aria-label="Skills section"
    >
      {/* Background grid */}
      <div className="absolute inset-0 cyber-grid" style={{ backgroundSize: '40px 40px', opacity: 0.5 }} />

      {/* Glow accent */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: 0, left: '50%', transform: 'translateX(-50%)',
          width: '60%', height: '1px',
          background: 'linear-gradient(90deg, transparent, var(--cyber-cyan), transparent)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section header */}
        <div className="mb-16">
          <p className="font-mono text-xs mb-3" style={{ color: 'var(--cyber-magenta)', letterSpacing: '0.3em' }}>
          // MODULE:01 
          </p>
          <h2 className="font-mono font-black text-4xl md:text-6xl" style={{ color: '#FFFFFF', letterSpacing: '-0.02em' }}>
            SKILL<span className="text-glow-cyan">_ARSENAL</span>
          </h2>
          <p className="font-mono text-sm mt-4" style={{ color: 'rgba(200,216,232,0.5)', maxWidth: 480 }}>
            CAPABILITIES_LOADED: {SKILL_GROUPS.reduce((a, g) => a + g.skills.length, 0)} MODULES ACTIVE
          </p>
        </div>

        {/* Bento grid of skill panels */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SKILL_GROUPS.map((group, gi) => (
            <div
              key={group.category}
              className="cyber-panel p-6 hud-corner relative"
              style={{
                border: `1px solid ${group.color}22`,
                '--hud-color': group.color,
              } as React.CSSProperties}
            >
              {/* Panel header */}
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-8 h-8 flex items-center justify-center"
                  style={{
                    background: `${group.color}15`,
                    border: `1px solid ${group.color}40`,
                    clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                  }}
                >
                  <Icon name={group.icon} size={16} className="" style={{ color: group.color }} />
                </div>
                <div>
                  <div className="font-mono text-xs font-bold" style={{ color: group.color, letterSpacing: '0.2em' }}>
                    {group.category}
                  </div>
                  <div className="font-mono text-[9px]" style={{ color: 'rgba(200,216,232,0.4)' }}>
                    {group.skills.length} MODULES
                  </div>
                </div>
                <div
                  className="ml-auto font-mono text-[9px] px-2 py-0.5"
                  style={{
                    background: `${group.color}12`,
                    border: `1px solid ${group.color}30`,
                    color: group.color,
                    letterSpacing: '0.15em',
                  }}
                >
                  ACTIVE
                </div>
              </div>

              {/* Skills */}
              <div className="space-y-4">
                {group.skills.map((skill, si) => (
                  <div key={skill.name}>
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="font-mono text-xs" style={{ color: 'rgba(200,216,232,0.8)' }}>
                        {skill.name}
                      </span>
                      <span className="font-mono text-[10px] font-bold" style={{ color: group.color }}>
                        {skill.level}%
                      </span>
                    </div>
                    <div
                      className="h-1.5 w-full relative overflow-hidden"
                      style={{ background: `${group.color}15` }}
                    >
                      <div
                        ref={(el) => { if (el) barsRef.current[gi * 5 + si] = el; }}
                        className="skill-bar-fill h-full"
                        style={{
                          background: `linear-gradient(90deg, ${group.color}, ${group.color}99)`,
                          boxShadow: `0 0 6px ${group.color}60`,
                          transform: animated ? `scaleX(${skill.level / 100})` : 'scaleX(0)',
                          transition: `transform ${1 + si * 0.1}s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${si * 0.08}s`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Tool badges */}
        <div className="mt-12">
          <p className="font-mono text-xs mb-4" style={{ color: 'rgba(200,216,232,0.4)', letterSpacing: '0.2em' }}>
            AUXILIARY_TOOLS:
          </p>
          <div className="flex flex-wrap gap-3">
            {TOOL_BADGES.map((tool) => (
              <div
                key={tool}
                className="font-mono text-xs px-3 py-1.5 transition-all duration-200 hover:border-neon-cyan cursor-default"
                style={{
                  background: 'rgba(0,212,255,0.04)',
                  border: '1px solid rgba(0,212,255,0.15)',
                  color: 'rgba(200,216,232,0.6)',
                  letterSpacing: '0.15em',
                  clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                }}
              >
                {tool}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}